import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "../../../assets/css/gamesCss/Tree/insertDeleteBinary.css";
import { Col } from "reactstrap";
import GameOverModal from "../../../components/GameOverModal";
import GameWonModal from "../../../components/GameWonModal";
import { useDarkMode } from "context/DarkModeProvider";
import "../../../assets/css/colors.css"
import { useLanguage } from "context/LanguageProvider";


const BinaryTreeInsertDelete = () => {
    const [currentStage, setCurrentStage] = useState(1);
    const [tree, setTree] = useState([]);
    const [availableNodes, setAvailableNodes] = useState([]);
    const [selectedTile, setSelectedTile] = useState(null);
    const [selectedNodeForDelete, setSelectedNodeForDelete] = useState(null);
    const [message, setMessage] = useState("");
    const [gameState, setGameState] = useState("playing");
    const [lives, setLives] = useState(3);
    const [showHint, setShowHint] = useState(false);
    const [wrongAttempts, setWrongAttempts] = useState(0);
    const [showGameOver, setShowGameOver] = useState(false);
    const [showGameWon, setShowGameWon] = useState(false);
    const [currentTask, setCurrentTask] = useState(null);
    const [tasksCompleted, setTasksCompleted] = useState(0);
    const { darkMode } = useDarkMode();
    const { selLanguage, toggleLanguage, isLoading } = useLanguage();


    const handlePlayAgain = () => {
        resetGame();
    };

    // Στάδια του παιχνιδιού
    const stages = {
        1: {
            operation: "insert",
            initialTree: [50, 30, 70, 20],
            nodesToInsert: [40, 60, 100, 101, 49],
            maxSize: 15,
            hints: [
                "Βρείτε την πρώτη κενή θέση σε level-order traversal",
                "Το level-order διασχίζει από αριστερά προς δεξιά, επίπεδο προς επίπεδο",
                "Η πρώτη null θέση είναι η σωστή θέση για εισαγωγή"
            ]
        },
        2: {
            operation: "delete",
            initialTree: [100, 50, 150, 25, 75, 125, 175],
            nodesToDelete: [75, 150],
            maxSize: 15,
            hints: [
                "Βρείτε τον τελευταίο κόμβο του δέντρου (σε level-order)",
                "Αντικαταστήστε τον κόμβο προς διαγραφή με τον τελευταίο κόμβο",
                "Στη συνέχεια αφαιρέστε την τελευταία θέση"
            ]
        }
    };

    useEffect(() => {
        initializeStage(currentStage);
    }, [currentStage]);

    const initializeStage = (stage) => {
        const stageData = stages[stage];
        const initialTree = [...stageData.initialTree];

        while (initialTree.length < stageData.maxSize) {
            initialTree.push(null);
        }

        setTree(initialTree);
        setTasksCompleted(0);
        setShowHint(false);
        setWrongAttempts(0);
        setSelectedTile(null);
        setSelectedNodeForDelete(null);

        if (stageData.operation === "insert") {
            setAvailableNodes([...stageData.nodesToInsert]);
            setCurrentTask({
                type: "insert",
                target: stageData.nodesToInsert[0]
            });
            setMessage(
                selLanguage
                    ? ``
                    : ``
            );

        } else {
            setAvailableNodes([]);
            setCurrentTask({
                type: "delete",
                target: stageData.nodesToDelete[0]
            });
            setMessage(
                selLanguage
                    ? ``
                    : ``);
        }
    };


    const isValidInsertPosition = (treeArray, index) => {
        if (index < 0 || index >= treeArray.length) return false;
        if (treeArray[index] !== null) return false;
        if (index === 0) return true;

        const parentIndex = Math.floor((index - 1) / 2);
        return treeArray[parentIndex] !== null;
    };


    // Βρίσκει την τελευταία μη-null θέση
    const findLastNodePosition = (treeArray) => {
        for (let i = treeArray.length - 1; i >= 0; i--) {
            if (treeArray[i] !== null) return i;
        }
        return -1;
    };

    const handleTileClick = (nodeValue) => {
        if (gameState !== "playing") return;
        if (currentTask.type !== "insert") return;

        setSelectedTile(nodeValue);

    };

    const handleTreePositionClick = (index) => {
        if (gameState !== "playing") return;

        if (currentTask.type === "insert") {
            handleInsertAtPosition(index);
        } else if (currentTask.type === "delete") {
            handleDeleteNode(index);
        }
    };

    const handleInsertAtPosition = (index) => {
        if (!selectedTile) {
            setMessage(
                selLanguage
                    ? "⚠️ Πρώτα επιλέξτε ένα πλακίδιο από τα διαθέσιμα!"
                    : "⚠️ First select a tile from the available ones!"
            );
            return;
        }

        // ΓΕΝΙΚΟΣ έλεγχος θέσης για δυαδικό δέντρο
        if (!isValidInsertPosition(tree, index)) {
            handleWrongAttempt(selLanguage ? "Λάθος θέση!" : "Wrong position!");
            return;
        }

        // === Εισαγωγή ===
        const newTree = [...tree];
        newTree[index] = selectedTile;
        setTree(newTree);

        // Βγάζουμε το πλακίδιο από τα διαθέσιμα
        const newAvailable = availableNodes.filter((n) => n !== selectedTile);
        setAvailableNodes(newAvailable);
        setSelectedTile(null);

        // === Progress logic του σταδίου ===
        const stageData = stages[currentStage];
        const newTasksCompleted = tasksCompleted + 1;
        setTasksCompleted(newTasksCompleted);

        if (newTasksCompleted >= stageData.nodesToInsert.length) {
            // Ολοκλήρωση σταδίου
            if (currentStage === 2) {
                setGameState("completed");
                setShowGameWon(true);
                setMessage(
                    selLanguage
                        ? "Συγχαρητήρια! Ολοκληρώσατε όλα τα στάδια!"
                        : "Congratulations! You completed all stages!"
                );
            } else {
                setMessage(
                    selLanguage
                        ? "Τέλεια! Προχωρώντας στο επόμενο στάδιο..."
                        : "Great! Moving to the next stage..."
                );
                setTimeout(() => {
                    setCurrentStage(currentStage + 1);
                }, 2500);
            }
        } else {
            const nextNode = stageData.nodesToInsert[newTasksCompleted];
            setCurrentTask({ type: "insert", target: nextNode });
            setMessage(
                selLanguage
                    ? ` Σωστά! Τώρα εισάγετε τον κόμβο ${nextNode}`
                    : ` Correct! Now insert node ${nextNode}`
            );
        }

        setShowHint(false);
        setWrongAttempts(0);
    };


    const handleDeleteNode = (index) => {
        const nodeValue = tree[index];

        if (nodeValue === null) {
            setMessage(selLanguage ? "Αυτή η θέση είναι κενή!" : "That position is empty!");
            return;
        }

        if (nodeValue !== currentTask.target) {
            setMessage(selLanguage ? `Πρέπει να διαγράψετε τον κόμβο ${currentTask.target}!` : `You need to delete node ${currentTask.target}!`);
            return;
        }

        // Βρίσκουμε τον τελευταίο κόμβο
        const lastPosition = findLastNodePosition(tree);

        if (lastPosition === index) {
            // Αν είναι ο τελευταίος, απλά τον αφαιρούμε
            const newTree = [...tree];
            newTree[index] = null;
            setTree(newTree);

            const newTasksCompleted = tasksCompleted + 1;
            setTasksCompleted(newTasksCompleted);

            const stageData = stages[currentStage];
            if (newTasksCompleted >= stageData.nodesToDelete.length) {
                if (currentStage === 2) {
                    setGameState("completed");
                    setShowGameWon(true);
                    setMessage(selLanguage ? "Συγχαρητήρια! Ολοκληρώσατε όλα τα στάδια!" : "Congratulations! You completed all stages!");
                } else {
                    setMessage(selLanguage ? "Τέλεια! Προχωρώντας στο επόμενο στάδιο..." : "Great! Moving to the next stage...");
                    setTimeout(() => {
                        setCurrentStage(currentStage + 1);
                    }, 2500);
                }
            } else {
                const nextNode = stageData.nodesToDelete[newTasksCompleted];
                setCurrentTask({ type: "delete", target: nextNode });
                setMessage(selLanguage ? `Σωστά! Τώρα διαγράψτε τον κόμβο ${nextNode}` : `Correct! Now delete node ${nextNode}`);
            }
        } else {
            // Πρέπει να αντικαταστήσουμε με τον τελευταίο
            const newTree = [...tree];
            newTree[index] = tree[lastPosition];
            newTree[lastPosition] = null;
            setTree(newTree);

            const newTasksCompleted = tasksCompleted + 1;
            setTasksCompleted(newTasksCompleted);

            const stageData = stages[currentStage];
            if (newTasksCompleted >= stageData.nodesToDelete.length) {
                setGameState("completed");
                setShowGameWon(true);
                setMessage(selLanguage ? "Συγχαρητήρια! Ολοκληρώσατε όλα τα στάδια!" : "Congratulations! You completed all stages!");
            } else {
                const nextNode = stageData.nodesToDelete[newTasksCompleted];
                setCurrentTask({ type: "delete", target: nextNode });
                setMessage(selLanguage ? `Σωστά! Τώρα διαγράψτε τον κόμβο ${nextNode}` : `Correct! Now delete node ${nextNode}`);
            }
        }
        setShowHint(false);
        setWrongAttempts(0);
    };

    const handleWrongAttempt = (msg) => {
        const newLives = lives - 1;
        setLives(newLives);
        setWrongAttempts(wrongAttempts + 1);
        setShowHint(true);

        if (newLives === 0) {
            setGameState("lost");
            setShowGameOver(true);
            setMessage(selLanguage ? "Έχασες όλες τις ζωές σου!" : "You lost all your lives!");
        } else {
            setMessage(selLanguage ? `${msg} Απομένουν ${newLives} ζωές.` : `${msg} You have ${newLives} lives left.`);
        }
    };

    const resetGame = () => {
        setCurrentStage(1);
        setLives(3);
        setGameState("playing");
        setMessage("");
        setShowHint(false);
        setWrongAttempts(0);
        setShowGameOver(false);
        setShowGameWon(false);
        initializeStage(1);
    };

    const resetCurrentStage = () => {
        initializeStage(currentStage);
    };

    const getHint = () => {
        const stageData = stages[currentStage];
        const hintIndex = Math.min(wrongAttempts, stageData.hints.length - 1);
        return stageData.hints[hintIndex];
    };

    const getNodePositions = (level, nodeIndex, totalLevels) => {
        const baseSpacing = 600;
        const levelSpacing = baseSpacing / Math.pow(2, level);
        const nodesInLevel = Math.pow(2, level);
        const indexInLevel = nodeIndex - (Math.pow(2, level) - 1);
        const startOffset = -(nodesInLevel - 1) * levelSpacing / 2;

        return {
            x: startOffset + indexInLevel * levelSpacing,
            y: level * 100
        };
    };

    const getNodeLevel = (index) => {
        return Math.floor(Math.log2(index + 1));
    };

    const renderEdges = () => {
        const edges = [];

        for (let i = 0; i < tree.length; i++) {
            if (tree[i] === null) continue;

            const leftChildIndex = 2 * i + 1;
            const rightChildIndex = 2 * i + 2;

            const parentLevel = getNodeLevel(i);
            const parentPos = getNodePositions(parentLevel, i, 5);

            if (leftChildIndex < tree.length && tree[leftChildIndex] !== null) {
                const childLevel = getNodeLevel(leftChildIndex);
                const childPos = getNodePositions(childLevel, leftChildIndex, 5);

                edges.push(
                    <line
                        key={`edge-${i}-${leftChildIndex}`}
                        x1={parentPos.x}
                        y1={parentPos.y + 25}
                        x2={childPos.x}
                        y2={childPos.y + 25}
                        stroke="#64748b"
                        strokeWidth="2"
                        opacity="0.6"
                    />
                );
            }

            if (rightChildIndex < tree.length && tree[rightChildIndex] !== null) {
                const childLevel = getNodeLevel(rightChildIndex);
                const childPos = getNodePositions(childLevel, rightChildIndex, 5);

                edges.push(
                    <line
                        key={`edge-${i}-${rightChildIndex}`}
                        x1={parentPos.x}
                        y1={parentPos.y + 25}
                        x2={childPos.x}
                        y2={childPos.y + 25}
                        stroke="#64748b"
                        strokeWidth="2"
                        opacity="0.6"
                    />
                );
            }
        }

        return edges;
    };

    const renderNode = (nodeValue, index) => {
        const level = getNodeLevel(index);
        const position = getNodePositions(level, index, 5);
        const isEmpty = nodeValue === null;
        const isTarget = currentTask && nodeValue === currentTask.target && currentTask.type === "delete";

        return (
            <g key={index}>
                <motion.circle
                    whileHover={{ scale: isEmpty ? 1.05 : 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    onClick={() => handleTreePositionClick(index)}
                    cx={position.x}
                    cy={position.y + 25}
                    r="25"
                    fill={isEmpty ? "#334155" : (isTarget ? "#ef4444" : "#3b82f6")}
                    stroke={isEmpty ? "#475569" : "rgba(255,255,255,0.1)"}
                    strokeWidth="2"
                    strokeDasharray={isEmpty ? "5,5" : "0"}
                    style={{
                        cursor: gameState === "playing" ? "pointer" : "default",
                        filter: isEmpty
                            ? "drop-shadow(0 2px 4px rgba(0,0,0,0.1))"
                            : isTarget
                                ? "drop-shadow(0 6px 15px rgba(239, 68, 68, 0.4))"
                                : "drop-shadow(0 6px 15px rgba(59, 130, 246, 0.4))",
                        transition: "all 0.3s ease"
                    }}
                />

                {!isEmpty && (
                    <text
                        x={position.x}
                        y={position.y + 30}
                        textAnchor="middle"
                        fill="white"
                        fontSize="14"
                        fontWeight="bold"
                        style={{ pointerEvents: "none" }}
                    >
                        {nodeValue}
                    </text>
                )}

                {isEmpty && (
                    <text
                        x={position.x}
                        y={position.y + 30}
                        textAnchor="middle"
                        fill="#64748b"
                        fontSize="12"
                        style={{ pointerEvents: "none" }}
                    >
                        ?
                    </text>
                )}
            </g>
        );
    };

    const renderTree = () => {
        const maxLevel = Math.floor(Math.log2(tree.length));
        const svgWidth = 800;
        const svgHeight = (maxLevel + 2) * 80 + 100;

        return (
            <div style={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
                overflowX: "auto",
                marginTop: "30px"
            }}>
                <svg
                    width={svgWidth}
                    height={svgHeight}
                    viewBox={`${-svgWidth / 2} 0 ${svgWidth} ${svgHeight}`}
                    style={{ minWidth: "600px" }}
                >
                    {renderEdges()}
                    {tree.map((nodeValue, index) => renderNode(nodeValue, index))}
                </svg>
            </div>
        );
    };

    useEffect(() => {
        if (message && !message.includes("🎯") && !message.includes("✋")) {
            const timer = setTimeout(() => {
                setMessage("");
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [message]);

    return (
        <div
            className='main-content binaryTreeInsertDeleteContainer'
            style={{
                background: darkMode ? "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #101724ff 100%)" : "var(--lightModeGameBackground)"
            }}
        >
            <GameOverModal
                isVisible={showGameOver}
                onPlayAgain={handlePlayAgain}
            />

            <GameWonModal
                isVisible={showGameWon}
                onPlayAgain={handlePlayAgain}
            />

            <Col xl="12" lg="12" md="12" sm="12" xs="12">
                <div className="max-w-7xl mx-auto" style={{ width: "100%" }}>

                    {/* Lives και Stage Info */}
                    <div style={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "15px",
                        flexWrap: "wrap",
                        gap: "10px",
                        marginTop: "15px"
                    }}>
                        <div style={{
                            color: darkMode ? "#fff" : "#1e293b",
                            padding: "12px 24px",
                            borderRadius: "12px",
                            fontWeight: "700",
                            fontSize: "18px",
                            border: darkMode ? "2px solid rgba(255, 255, 255, 0.2)" : "2px solid #1e293b",
                            display: "flex",
                            alignItems: "center",
                            gap: "10px"
                        }}>
                            {
                                selLanguage
                                    ? `Στάδιο ${currentStage}/2`
                                    : `Stage ${currentStage}/2`
                            }

                        </div>

                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            marginRight: "10px"
                        }}>

                            <div style={{ display: "flex", gap: "6px" }}>
                                {[1, 2, 3].map((heartNum) => (
                                    <div
                                        key={heartNum}
                                        style={{
                                            fontSize: "24px",
                                            filter: lives >= heartNum
                                                ? "drop-shadow(0 4px 8px rgba(220, 38, 38, 0.4))"
                                                : "grayscale(100%) opacity(0.3)",
                                        }}
                                    >
                                        ❤️
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Description */}
                    <div className="binaryTreeInsertDeletePron">
                        <div>
                            {stages[currentStage].operation === "insert"
                                ? selLanguage ? "Επιλέξτε ένα πλακίδιο και κλικάρετε την πρώτη κενή θέση στο δέντρο (level-order)" : "Select a tile and click the first empty position in the tree (level-order)"
                                : selLanguage ? "Κλικάρετε τον κόμβο προς διαγραφή. Θα αντικατασταθεί με τον τελευταίο κόμβο." : "Click the node to delete. It will be replaced by the last node."}
                        </div>
                    </div>


                    {/* Tree Visualization */}
                    <div className="binaryTreeInsertDeleteVisual">
                        {renderTree()}
                    </div>

                    {/* Available Tiles για Insert */}
                    {stages[currentStage].operation === "insert" && availableNodes.length > 0 && (
                        <div className="binaryTreeInsertDeleteTiles">
                            <div style={{ marginBottom: "10px", fontWeight: "600", color: "white" }}>
                                {selLanguage ? "Διαθέσιμα Πλακίδια:" : "Available node:"}
                            </div>
                            <div style={{ display: "flex", gap: "10px", justifyContent: "center", flexWrap: "wrap" }}>
                                {availableNodes.map((node, idx) => (
                                    <motion.div
                                        key={idx}
                                        whileHover={{ scale: 1.1 }}
                                        whileTap={{ scale: 0.95 }}
                                        onClick={() => handleTileClick(node)}
                                        className="binaryTreeInsertDeleteTile"
                                        style={{
                                            width: "48px",
                                            height: "48px",
                                            borderRadius: "50%",
                                            background: selectedTile === node
                                                ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                                                : "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                                            border: selectedTile === node ? "3px solid #34d399" : "2px solid rgba(255,255,255,0.2)"
                                        }}
                                    >
                                        {node}
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Messages */}
                    <AnimatePresence>
                        {message && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                style={{
                                    textAlign: "center",
                                    padding: "20px",
                                    borderRadius: "16px",
                                    marginBottom: "20px",
                                    background: message.includes("Σωστά") || message.includes("Correct") || message.includes("Τέλεια") || message.includes("Great") || message.includes("Συγχαρητήρια") || message.includes("Congratulations")
                                        ? "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)"
                                        : message.includes("Λάθος") || message.includes("Wrong") || message.includes("Έχασες") || message.includes("lost") || message.includes("Απομένουν") || message.includes("lives left")
                                            ? "linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)"
                                            : "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)",
                                    border: `2px solid ${message.includes("Σωστά") || message.includes("Correct") || message.includes("Τέλεια") || message.includes("Great") || message.includes("Συγχαρητήρια") || message.includes("Congratulations") ? "#10b981" :
                                        message.includes("Λάθος") || message.includes("Wrong") || message.includes("Έχασες") || message.includes("lost") || message.includes("Απομένουν") || message.includes("lives left") ? "#ef4444" : "#3b82f6"
                                        }`,
                                    color: message.includes("Σωστά") || message.includes("Correct") || message.includes("Τέλεια") || message.includes("Great") || message.includes("Συγχαρητήρια") || message.includes("Congratulations") ? "#059669" :
                                        message.includes("Λάθος") || message.includes("Wrong") || message.includes("Έχασες") || message.includes("lost") || message.includes("Απομένουν") || message.includes("lives left") ? "#dc2626" : "#1d4ed8",
                                    fontWeight: 600,
                                    fontSize: "1rem",
                                    maxWidth: "700px",
                                    margin: "0 auto 20px auto",
                                    width: "90%"
                                }}
                            >
                                {message}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Buttons */}
                    <div className="binaryTreeInsertDeleteButtonsSpace">
                        <motion.button
                            className="binaryTreeInsertDeleteReset"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={resetCurrentStage}
                        >
                            {selLanguage ? "Επαναφορά" : "Reset"}
                        </motion.button>

                        {(gameState === "lost" || gameState === "completed") && (
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={resetGame}
                                style={{
                                    background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "16px",
                                    padding: "14px 28px",
                                    fontSize: "1rem",
                                    fontWeight: 600,
                                    cursor: "pointer",
                                    boxShadow: "0 10px 25px rgba(16, 185, 129, 0.3)",
                                    transition: "all 0.3s ease",
                                    flex: 1,
                                    maxWidth: "150px"
                                }}
                            >
                                {selLanguage ? "Νέο Παιχνίδι" : "New Game"}
                            </motion.button>
                        )}
                    </div>
                </div>
            </Col>
        </div>
    );
};

export default BinaryTreeInsertDelete;